import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ShoppingBag,
  Heart,
  MessageCircle,
  ChevronRight,
  ShieldCheck,
  Truck,
  RefreshCw,
  Award,
  Gem,
  Zap
} from 'lucide-react';
import API from '../utils/api';
import config from '../utils/config';
import { useCart } from '../context/AppContext';
import toast from 'react-hot-toast';

const FALLBACK = {
  _id:'1',
  name:'18K Diamond Solitaire Ring',
  slug:'18k-diamond-solitaire-ring',
  category:'rings',
  price:85000,
  discountPrice:72000,
  stock:12,
  tags:['bestseller'],
  description:'A stunning 18K yellow gold ring featuring a brilliant-cut 0.5ct solitaire diamond. GIA certified. Perfect for engagements and special occasions.',
  materialDetails:{
    metal:'18K Yellow Gold',
    stone:'0.5ct Round Diamond',
    clarity:'VS1',
    color:'E-F',
    weight:'4.2g',
    certificate:'GIA Certified'
  },
  images:[
    {url:'https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=900'},
    {url:'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=900'},
  ]
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [wished, setWished] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await API.get(`/products/${id}`);
        setProduct(data);
      } catch {
        setProduct(FALLBACK);
      }

      setLoading(false);
    }

    load();
    window.scrollTo(0, 0);
  }, [id]);

  if (loading) {
    return (
      <div className="jewel-loader" style={{ marginTop: 120 }}>
        <div className="jewel-loader-ring" />
        <span>Curating jewellery...</span>
      </div>
    );
  }

  if (!product) return null;

  const images = product.images?.length ? product.images : [{ url: '/images/jewelry-placeholder.jpg' }];

  const disc =
    product.discountPrice && product.price > product.discountPrice
      ? Math.round(((product.price - product.discountPrice) / product.price) * 100)
      : 0;

  const stockStatus =
    product.stock === 0
      ? 'Out of Stock'
      : product.stock <= 5
      ? 'Low Stock'
      : 'In Stock';

  const stockClass =
    product.stock === 0
      ? 'stock-out'
      : product.stock <= 5
      ? 'stock-low'
      : 'stock-in-stock';

  const mdEntries = Object.entries(product.materialDetails || {}).filter(([, v]) => v);

  return (
    <div className="page-enter">
      <style>{`
        .product-page-wrap {
          padding-top: 72px;
        }

        .pd-breadcrumb {
          background: rgba(245,240,232,0.88);
          padding: 12px 24px;
          font-size: 12px;
          color: var(--text-light);
          display: flex;
          gap: 6px;
          align-items: center;
          border-bottom: 1px solid rgba(201,169,110,0.18);
        }

        .pd-breadcrumb span {
          white-space: nowrap;
        }

        .pd-clickable {
          cursor: pointer;
          transition: color .2s ease;
        }

        .pd-clickable:hover {
          color: var(--maroon);
        }

        .pd-grid {
          max-width: 1180px;
          margin: 0 auto;
          padding: 52px 24px 70px;
          display: grid;
          grid-template-columns: 1.05fr .95fr;
          gap: 64px;
          align-items: start;
        }

        .pd-image-card {
          width: 100%;
          height: 540px;
          border-radius: 8px;
          overflow: hidden;
          background: linear-gradient(135deg,#F7EFE4,#E8D4BD);
          border: 1px solid rgba(201,169,110,0.22);
          box-shadow: 0 18px 45px rgba(107,27,42,0.08);
          position: relative;
        }

        .pd-main-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform .65s ease;
        }

        .pd-image-card:hover .pd-main-img {
          transform: scale(1.08);
        }

        .pd-image-card::after {
          content: "";
          position: absolute;
          top: 0;
          left: -130%;
          width: 70%;
          height: 100%;
          background: linear-gradient(110deg, transparent, rgba(255,255,255,0.32), transparent);
          transform: skewX(-18deg);
          transition: left .8s ease;
          pointer-events: none;
        }

        .pd-image-card:hover::after {
          left: 130%;
        }

        .pd-thumbs {
          display: flex;
          gap: 10px;
          margin-top: 14px;
          flex-wrap: wrap;
        }

        .pd-thumb {
          width: 76px;
          height: 76px;
          border-radius: 6px;
          overflow: hidden;
          cursor: pointer;
          border: 2px solid var(--border);
          transition: all .25s ease;
          background: var(--cream);
        }

        .pd-thumb.active {
          border-color: var(--maroon);
          box-shadow: 0 8px 20px rgba(107,27,42,0.12);
        }

        .pd-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .pd-tagline {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 14px;
        }

        .pd-title {
          font-family: "Cormorant Garamond", serif;
          font-size: clamp(34px,4vw,52px);
          font-weight: 300;
          color: var(--text-dark);
          line-height: 1.1;
          margin-bottom: 14px;
        }

        .pd-price-row {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-bottom: 12px;
        }

        .pd-price {
          font-family: "Cormorant Garamond", serif;
          font-size: 36px;
          font-weight: 500;
          color: var(--maroon);
        }

        .pd-original {
          font-size: 18px;
          color: var(--text-light);
          text-decoration: line-through;
        }

        .pd-certified {
          display: flex;
          gap: 10px;
          margin: 16px 0 24px;
          flex-wrap: wrap;
        }

        .pd-certified span {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #fff;
          border: 1px solid rgba(201,169,110,0.25);
          color: var(--text-mid);
          padding: 7px 10px;
          border-radius: 30px;
          font-size: 12px;
        }

        .pd-desc {
          font-size: 15px;
          color: var(--text-mid);
          line-height: 1.9;
          margin-bottom: 28px;
        }

        .pd-actions {
          display: flex;
          flex-direction: column;
          gap: 11px;
          margin-bottom: 24px;
        }

        .pd-trust-box {
          margin-top: 20px;
          padding: 20px;
          background: var(--cream);
          border: 1px solid rgba(201,169,110,0.22);
          border-radius: 8px;
          display: grid;
          grid-template-columns: repeat(3,1fr);
          gap: 12px;
          text-align: center;
        }

        .pd-trust-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          color: var(--text-mid);
        }

        .pd-trust-item strong {
          color: var(--maroon);
          font-size: 13px;
          letter-spacing: .5px;
        }

        .pd-trust-item p {
          font-size: 12px;
          color: var(--text-light);
        }

        .pd-details {
          border-top: 1px solid var(--border);
          padding-top: 16px;
          margin-top: 18px;
        }

        .pd-details summary {
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          padding-bottom: 12px;
          user-select: none;
          color: var(--text-dark);
        }

        .pd-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 8px;
          background: white;
          border: 1px solid var(--border);
        }

        .pd-table tr {
          border-bottom: 1px solid var(--border);
        }

        .pd-table td {
          padding: 10px 12px;
          font-size: 13px;
        }

        .pd-table td:first-child {
          color: var(--text-light);
          text-transform: capitalize;
          width: 40%;
          background: var(--cream);
        }

        .pd-shipping-copy {
          font-size: 13px;
          color: var(--text-mid);
          line-height: 1.8;
          padding-top: 8px;
        }

        @media(max-width: 850px) {
          .pd-grid {
            grid-template-columns: 1fr;
            gap: 34px;
            padding: 36px 20px 60px;
          }

          .pd-image-card {
            height: 420px;
          }

          .pd-trust-box {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="product-page-wrap">
        <div className="pd-breadcrumb">
          <span className="pd-clickable" onClick={() => navigate('/')}>Home</span>
          <ChevronRight size={12} />
          <span className="pd-clickable" onClick={() => navigate('/collections')}>Collections</span>
          <ChevronRight size={12} />
          <span style={{ color: 'var(--text-dark)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {product.name}
          </span>
        </div>

        <div className="pd-grid">
          <div>
            <div className="pd-image-card">
              <img
                src={images[activeImg]?.url || '/images/jewelry-placeholder.jpg'}
                alt={product.name}
                className="pd-main-img"
                onError={(e) => {
                  e.currentTarget.src = '/images/jewelry-placeholder.jpg';
                }}
              />
            </div>

            {images.length > 1 && (
              <div className="pd-thumbs">
                {images.map((img, i) => (
                  <div
                    key={i}
                    className={`pd-thumb${activeImg === i ? ' active' : ''}`}
                    onClick={() => setActiveImg(i)}
                  >
                    <img
                      src={img.url}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.src = '/images/jewelry-placeholder.jpg';
                      }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <span className="tag badge-bestseller pd-tagline">
              <Gem size={12} />
              {product.category}
            </span>

            <h1 className="pd-title">{product.name}</h1>

            <div className="pd-price-row">
              <span className="pd-price">
                ₹{(product.discountPrice || product.price).toLocaleString()}
              </span>

              {disc > 0 && (
                <>
                  <span className="pd-original">₹{product.price.toLocaleString()}</span>
                  <span className="tag badge-sale">{disc}% off</span>
                </>
              )}
            </div>

            <span className={`tag ${stockClass}`} style={{ marginBottom: 10, display: 'inline-block' }}>
              {stockStatus}
            </span>

            <div className="pd-certified">
              <span><ShieldCheck size={14} /> </span>
              <span><Award size={14} /> </span>
              <span><RefreshCw size={14} /> </span>
            </div>

            <p className="pd-desc">{product.description}</p>

            <div className="pd-actions">
             <button
  className="btn btn-primary btn-full btn-lg"
  onClick={() => {
    if (product.stock <= 0) {
      toast.error('This product is currently out of stock');
      return;
    }

    addToCart(product);
  }}
  disabled={product.stock <= 0}
>
  <ShoppingBag size={16} />
  {product.stock <= 0 ? 'Out of Stock' : 'Add to Cart'}
</button>

             <button
  className="btn btn-gold btn-full btn-lg"
  onClick={() => {
    if (product.stock <= 0) {
      toast.error('This product is currently out of stock');
      return;
    }

    addToCart(product);
    navigate('/checkout');
  }}
  disabled={product.stock <= 0}
>
  <Zap size={16} />
  {product.stock <= 0 ? 'Out of Stock' : 'Buy Now'}
</button>

              <a
                href={`https://wa.me/${config.whatsappNumber}?text=Hi!%20I%20am%20interested%20in%20${encodeURIComponent(product.name)}%20-%20₹${(product.discountPrice || product.price).toLocaleString()}`}
                target="_blank"
                rel="noreferrer"
                className="btn btn-whatsapp btn-full btn-lg"
                style={{ textDecoration: 'none' }}
              >
                <MessageCircle size={16} />
                Enquire on WhatsApp
              </a>

              <button
                className={`btn btn-full${wished ? ' btn-primary' : ' btn-outline'}`}
                onClick={() => {
                  setWished((w) => !w);
                  toast.success(wished ? 'Removed from wishlist' : 'Added to wishlist');
                }}
              >
                <Heart size={16} fill={wished ? 'white' : 'none'} />
                {wished ? 'Wishlisted' : 'Add to Wishlist'}
              </button>
            </div>

            <div className="pd-trust-box">
              <div className="pd-trust-item">
                <Truck size={20} color="var(--maroon)" />
                <strong>Charges Included</strong>
                <p>Insured delivery</p>
              </div>

              <div className="pd-trust-item">
                <RefreshCw size={20} color="var(--maroon)" />
                <strong>10 Day Returns</strong>
                <p>Easy exchange</p>
              </div>

              <div className="pd-trust-item">
                <ShieldCheck size={20} color="var(--maroon)" />
                <strong>100% Certified</strong>
                <p>Quality assured</p>
              </div>
            </div>

            {mdEntries.length > 0 && (
              <details className="pd-details">
                <summary>Material Details</summary>

                <table className="pd-table">
                  <tbody>
                    {mdEntries.map(([k, v]) => (
                      <tr key={k}>
                        <td>{k}</td>
                        <td>{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            <details className="pd-details">
              <summary>Shipping & Returns</summary>

              <p className="pd-shipping-copy">
                Return & Replacement Policy
We take great care in packaging and inspecting all stainless steel jewelry before shipment.
Returns or replacements will only be accepted if the product is received damaged or defective.
To be eligible for a claim:
Customers must record a continuous unboxing video from the moment the sealed package is opened until the product is fully inspected.
The video must clearly show the package condition, shipping label, and the damaged or defective item.
Claims submitted without an unboxing video may not be accepted.
Any damage claim must be reported within 24–48 hours of delivery.
We do not accept returns, exchanges, or refunds for:
Change of mind.
Incorrect ordering by the customer.
Normal wear and tear.
Products damaged after delivery.
If your item arrives damaged, please contact us at anvithdazzle@gmail.com with the unboxing video and photos of the damaged product. After verification, we will arrange a replacement or refund at our discretion.
              </p>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
}